import { create } from 'zustand';
import { FileReceiver } from '../lib/transfer/receiver';
import type { FileMetadata } from '../lib/transfer/protocol';
import { supportsFileSystemAccess } from '../lib/transfer/sink';
import type { IntegrityResult, TransferPhase, TransferStats } from '../lib/transfer/types';
import { SignalingClient } from '../lib/webrtc/signaling';

interface ReceiverState {
  phase: TransferPhase;
  roomId: string | null;
  meta: FileMetadata | null;
  stats: TransferStats | null;
  /** Set once the user must choose a save destination (user gesture). */
  needsSaveLocation: boolean;
  canStreamToDisk: boolean;
  hasResumable: boolean;
  integrity: IntegrityResult;
  downloadUrl: string | null;
  downloadName: string | null;
  error: string | null;

  connect: (roomId: string) => void;
  chooseSaveLocation: (mode: 'disk' | 'memory') => Promise<void>;
  cancel: () => void;
  reset: () => void;
}

let receiver: FileReceiver | null = null;
let signaling: SignalingClient | null = null;

function teardown(): void {
  receiver?.destroy();
  signaling?.close();
  receiver = null;
  signaling = null;
}

export const useReceiverStore = create<ReceiverState>((set) => ({
  phase: 'idle',
  roomId: null,
  meta: null,
  stats: null,
  needsSaveLocation: false,
  canStreamToDisk: supportsFileSystemAccess(),
  hasResumable: false,
  integrity: 'pending',
  downloadUrl: null,
  downloadName: null,
  error: null,

  connect: (roomId: string) => {
    teardown();
    set({
      roomId,
      phase: 'connecting',
      meta: null,
      stats: null,
      needsSaveLocation: false,
      integrity: 'pending',
      downloadUrl: null,
      downloadName: null,
      error: null,
    });

    signaling = new SignalingClient(roomId, 'receiver');
    receiver = new FileReceiver(signaling, roomId);

    receiver.on('phase', (phase) => set({ phase }));
    receiver.on('meta', (meta) => set({ meta }));
    receiver.on('progress', (stats) => set({ stats }));
    receiver.on('needsSaveLocation', () =>
      set({ needsSaveLocation: true, hasResumable: receiver?.hasResumableCheckpoint ?? false }),
    );
    receiver.on('done', ({ integrity, objectUrl, fileName }) =>
      set({
        integrity,
        downloadUrl: objectUrl ?? null,
        downloadName: fileName,
        needsSaveLocation: false,
      }),
    );
    receiver.on('error', ({ message }) => set({ error: message, phase: 'error' }));

    signaling.connect();
    void receiver.start();
  },

  chooseSaveLocation: async (mode: 'disk' | 'memory') => {
    if (!receiver) return;
    try {
      await receiver.provideSaveLocation(mode);
      set({ needsSaveLocation: false });
    } catch (err) {
      // AbortError = user dismissed the file picker; stay on the prompt.
      const name = (err as { name?: string }).name;
      if (name !== 'AbortError') {
        set({ error: (err as Error).message });
      }
    }
  },

  cancel: () => {
    receiver?.cancel();
    set({ phase: 'cancelled' });
  },

  reset: () => {
    teardown();
    set({
      phase: 'idle',
      roomId: null,
      meta: null,
      stats: null,
      needsSaveLocation: false,
      integrity: 'pending',
      downloadUrl: null,
      downloadName: null,
      error: null,
    });
  },
}));
